import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const SourceManager = ({ sources, onUpdate, onRefresh }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    source_type: 'youtube',
    importance: 'medium',
    monitoring_frequency: 1440,
    min_duration_seconds: 0,
    max_duration_seconds: 0,
    exclude_shorts: true,
    min_view_count: 0
  });

  const sourceTypes = [
    { value: 'youtube', label: 'YouTube Channel', icon: '📺' },
    { value: 'news', label: 'News Website', icon: '📰' },
    { value: 'blog', label: 'Blog', icon: '✍️' },
    { value: 'social', label: 'Social Media', icon: '📱' },
    { value: 'other', label: 'Other', icon: '🔗' }
  ];

  const importanceLevels = [
    { value: 'low', label: 'Low', color: 'text-gray-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-red-600' }
  ];

  const refreshFrequencies = [
    { value: 60, label: '1 hour' },
    { value: 240, label: '4 hours' },
    { value: 480, label: '8 hours' },
    { value: 1440, label: '1 day' },
    { value: 10080, label: '1 week' }
  ];

  useEffect(() => {
    if (editingId) {
      const source = sources.find(s => s.id === editingId);
      if (source) {
        setFormData({
          name: source.name || '',
          url: source.url || '',
          source_type: source.source_type || 'youtube',
          importance: source.importance || 'medium',
          monitoring_frequency: source.refresh_frequency || 1440,
          min_duration_seconds: source.min_duration_seconds || 0,
          max_duration_seconds: source.max_duration_seconds || 0,
          exclude_shorts: source.exclude_shorts !== false,
          min_view_count: source.min_view_count || 0
        });
      }
    }
  }, [editingId, sources]);

  const extractChannelId = (url) => {
    if (!url) return '';
    
    // Handle @channelname format
    const handleMatch = url.match(/youtube\.com\/@([^\/\?]+)/);
    if (handleMatch) {
      return handleMatch[1];
    }
    
    // Handle /channel/ format
    const channelMatch = url.match(/youtube\.com\/channel\/([^\/\?]+)/);
    if (channelMatch) {
      return channelMatch[1];
    }
    
    // Handle /c/ format
    const customMatch = url.match(/youtube\.com\/c\/([^\/\?]+)/);
    if (customMatch) {
      return customMatch[1];
    }
    
    // Handle /user/ format
    const userMatch = url.match(/youtube\.com\/user\/([^\/\?]+)/);
    if (userMatch) {
      return userMatch[1];
    }
    
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let payload = { ...formData };
      
      // For YouTube sources, extract channel ID
      if (formData.source_type === 'youtube') {
        const channelId = extractChannelId(formData.url);
        if (!channelId) {
          alert('Please enter a valid YouTube channel URL');
          return;
        }
        payload.channel_id = channelId;
      }
      
      // Ensure monitoring_frequency is a number
      if (payload.monitoring_frequency) {
        payload.monitoring_frequency = parseInt(payload.monitoring_frequency);
      }

      if (payload.min_duration_seconds) {
        payload.min_duration_seconds = parseInt(payload.min_duration_seconds);
      }
      if (payload.max_duration_seconds) {
        payload.max_duration_seconds = parseInt(payload.max_duration_seconds);
      }
      if (payload.min_view_count) {
        payload.min_view_count = parseInt(payload.min_view_count);
      }
      
      if (editingId) {
        // Update existing source
        const response = await fetch(`${API_ENDPOINTS.CONTENT_SOURCES}${editingId}/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          onUpdate();
          resetForm();
        } else {
          console.error('Failed to update source');
        }
      } else {
        // Create new source
        const response = await fetch(API_ENDPOINTS.CONTENT_SOURCES, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          onUpdate();
          resetForm();
        } else {
          console.error('Failed to create source');
        }
      }
    } catch (error) {
      console.error('Error saving source:', error);
    }
  };

  const handleEdit = (source) => {
    setEditingId(source.id);
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this source?')) {
      try {
        const response = await fetch(`${API_ENDPOINTS.CONTENT_SOURCES}${id}/`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          onUpdate();
        } else {
          console.error('Failed to delete source');
        }
      } catch (error) {
        console.error('Error deleting source:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      source_type: 'youtube',
      importance: 'medium',
      monitoring_frequency: 1440,
      min_duration_seconds: 0,
      max_duration_seconds: 0,
      exclude_shorts: true,
      min_view_count: 0
    });
    setEditingId(null);
    setIsAdding(false);
  };

  const getSourceIcon = (sourceType) => {
    const source = sourceTypes.find(s => s.value === sourceType);
    return source ? source.icon : '🔗';
  };

  const getImportanceColor = (importance) => {
    // Handle both numeric values and string labels
    let searchValue = importance;
    
    // If it's a numeric value, convert to label first
    if (typeof importance === 'number') {
      if (importance === 1) searchValue = 'low';
      else if (importance === 2) searchValue = 'medium';
      else if (importance === 3) searchValue = 'high';
    }
    
    const level = importanceLevels.find(l => l.value === searchValue);
    return level ? level.color : 'text-gray-500';
  };

  const getImportanceLabel = (importance) => {
    // Handle numeric importance values (1=low, 2=medium, 3=high)
    if (typeof importance === 'number') {
      if (importance === 1) return 'low';
      if (importance === 2) return 'medium';
      if (importance === 3) return 'high';
    }
    
    // Handle string values
    if (typeof importance === 'string') {
      return importance.toLowerCase();
    }
    
    return 'medium'; // Default fallback
  };

  const getRefreshFrequencyLabel = (frequency) => {
    // Handle null, undefined, or invalid frequency values
    if (!frequency || isNaN(frequency)) {
      return '1 day'; // Default fallback
    }
    
    const freq = refreshFrequencies.find(f => f.value === frequency);
    return freq ? freq.label : `${frequency} minutes`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'No limit';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Content Sources</h2>
          <p className="text-gray-600">Manage YouTube channels and websites for monitoring</p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Source</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {isAdding && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Source' : 'Add New Source'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter source name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source Type *
                </label>
                <select
                  required
                  value={formData.source_type}
                  onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {sourceTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL *
              </label>
              <input
                type="url"
                required
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={formData.source_type === 'youtube' ? 'https://www.youtube.com/@channelname' : 'https://example.com'}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Importance Level
                </label>
                <select
                  value={formData.importance}
                  onChange={(e) => setFormData({ ...formData, importance: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {importanceLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refresh Frequency
                </label>
                <select
                  value={formData.monitoring_frequency}
                  onChange={(e) => setFormData({ ...formData, monitoring_frequency: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {refreshFrequencies.map(freq => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* YouTube-specific filtering options */}
            {formData.source_type === 'youtube' && (
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-700 mb-3">Content Filtering</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.min_duration_seconds}
                      onChange={(e) => setFormData({...formData, min_duration_seconds: e.target.value})}
                      placeholder="0 = no minimum"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_duration_seconds}
                      onChange={(e) => setFormData({...formData, max_duration_seconds: e.target.value})}
                      placeholder="0 = no maximum"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min View Count
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.min_view_count}
                      onChange={(e) => setFormData({...formData, min_view_count: e.target.value})}
                      placeholder="0 = no minimum"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="exclude_shorts"
                      checked={formData.exclude_shorts}
                      onChange={(e) => setFormData({...formData, exclude_shorts: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="exclude_shorts" className="ml-2 block text-sm text-gray-700">
                      Exclude YouTube Shorts (≤60 seconds)
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingId ? 'Update' : 'Add'} Source
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sources List */}
      <div className="bg-white rounded-lg border border-gray-200 flex-1 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">All Sources ({sources.length})</h3>
            <button
              onClick={onRefresh}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Importance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Filters
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Check
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sources.map((source) => (
                <tr key={source.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{source.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{source.url}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getSourceIcon(source.source_type)}</span>
                      <span className="text-sm text-gray-900 capitalize">
                        {source.source_type === 'youtube' ? 'YouTube' : source.source_type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getImportanceColor(getImportanceLabel(source.importance))} bg-gray-100`}>
                      {getImportanceLabel(source.importance)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {getRefreshFrequencyLabel(source.refresh_frequency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {source.source_type === 'youtube' && (
                      <div className="text-xs space-y-1">
                        {source.min_duration_seconds > 0 && (
                          <div>Min: {formatDuration(source.min_duration_seconds)}</div>
                        )}
                        {source.max_duration_seconds > 0 && (
                          <div>Max: {formatDuration(source.max_duration_seconds)}</div>
                        )}
                        {source.exclude_shorts && <div>No Shorts</div>}
                        {source.min_view_count > 0 && (
                          <div>Min Views: {source.min_view_count.toLocaleString()}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {source.last_checked ? new Date(source.last_checked).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(source)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(source.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {sources.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📺</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sources yet</h3>
              <p className="text-gray-500">Add your first YouTube channel or website to start monitoring content.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceManager;
